import React from 'react';

/**
 * 6love ChartPanel — the titled surface every analytics chart sits in: a header
 * (title + optional subtitle), an optional icon chip and right-aligned action
 * slot, and a soft-shadowed white card body. This is the app implementation of
 * the design system's `ChartPanel`; it replaces the Card that AnalyticsTab used
 * to re-implement inline.
 */
interface ChartPanelProps {
  /** Header title. */
  title?: string;
  /** Secondary line under the title. */
  subtitle?: string;
  /** Right-aligned node — a unit toggle, refresh button, or legend. */
  action?: React.ReactNode;
  /** Optional icon node rendered in a green chip before the title. */
  icon?: React.ReactNode;
  /** `"full"` stretches the panel across both columns of the analytics grid. */
  span?: 'full';
  /** Body padding. @default "md" */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Extra classes on the card root. */
  className?: string;
  /** Chart / content. */
  children: React.ReactNode;
}

const PADDING: Record<NonNullable<ChartPanelProps['padding']>, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-6',
};

const ChartPanel: React.FC<ChartPanelProps> = ({
  title,
  subtitle,
  action,
  icon,
  span,
  padding = 'md',
  className,
  children,
}) => (
  <div
    className={[
      'bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50',
      PADDING[padding],
      span === 'full' ? 'lg:col-span-2' : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {(title || action) && (
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {icon && (
            <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400 shrink-0">
              {icon}
            </div>
          )}
          <div className="min-w-0">
            {title && (
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    )}
    {children}
  </div>
);

export default ChartPanel;
